import { useEffect, useState, useCallback, useContext } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import type { GridSortModel } from '@mui/x-data-grid';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';

import { useForm } from 'react-hook-form';

import { Image, Team, TeamMember } from '@vyce/core/src/types';

import { CreateTeamData, GetCompanyTeamsMembersDataRequest } from '@vyce/core/src/api/types';
import {
  changeTeamManagersRequest,
  deleteLogoRequest,
  deleteTeamMembersRequest,
  deleteTeamRequest,
  getTeamMembersRequest,
  getTeamRequest,
  updateLogoRequest,
  updateTeamRequest,
  addTeamMembersRequest,
  getTeamsCandidatesRequest,
} from '@vyce/core/src/api/teams';
import { getUrlItems } from '@vyce/core/src/utils/url';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useTable } from '@vyce/core/src/hooks/useTable';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

import { useTypedSelector } from '../../../hooks';
import { defaultValues } from '../config';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { formatSortModel } from '@vyce/core/src/utils';
import { getStaffRequest } from '@vyce/core/src/api/companies';

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'desc' }];

export const useTeamPageData = () => {
  // dialogs section
  const [open, setOpen] = useState<boolean>(false);
  const [openDeleteTeamDialog, setOpenDeleteTeamDialog] = useState<boolean>(false);

  // requests section
  const [loading, setLoading] = useState<boolean>(false);
  const [teamData, setTeamData] = useState<Team>();

  // common section
  const [logo, setLogo] = useState<Image | undefined>(undefined);
  const [selectionModel, setSelectionModel] = useState<string[]>([]);

  const {
    sortModel,
    substring,
    total,
    setOffset,
    setTotal,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });
  const { user } = useTypedSelector(state => state);
  const { updateChat, removeUsersFromChat, removeChat } = useChatRequests({
    uid: user.uuid,
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    phone: user.phone,
    avatarUrl: user.photo?.url,
  });

  const { team } = useParams<{ team: string }>();
  const history = useHistory();
  const { selectedCompany } = useTypedSelector(state => state.helper);

  const pathname = history.location.pathname;
  const selectedCompanyId = selectedCompany?.uuid ?? '';
  const teamDataId = teamData?.uuid ?? '';
  const { handleServerError, showNotification } = useContext(NotificationContext);

  const methods = useForm<CreateTeamData>({
    defaultValues,
  });

  const getTeamMembers = useCallback(async () => {
    if (!selectedCompanyId) {
      return;
    }
    setLoading(true);
    const { id: teamId } = getUrlItems(team);
    try {
      const requestData: GetCompanyTeamsMembersDataRequest = {
        offset: 0,
        limit: GRID_PAGE_SIZE,
        substring,
        order_by: formatSortModel<TeamMember>(sortModel),
      };

      const {
        data: { count },
      } = await getTeamMembersRequest(selectedCompanyId, teamId, requestData);
      setTotal(count);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  }, [selectedCompanyId, sortModel, substring, team]);

  const handleSubmit = useCallback(
    async (data: CreateTeamData) => {
      if (!selectedCompanyId || !teamDataId || !user?.uuid) {
        return;
      }
      try {
        let newTeamData = null;
        const { logo, ...restData } = data;
        const restOldData = omit(teamData, ['uuid', 'logo']);
        const { logo: oldLogo } = teamData ?? {};
        const isEqualData = isEqual(restData, restOldData);
        const isEqualLogo = isEqual(logo, oldLogo);

        if (!isEqualData) {
          const updatePayload = omit(restData, ['locations', 'teamManagers']) as CreateTeamData;
          const { data } = await updateTeamRequest(selectedCompanyId, teamDataId, updatePayload);
          newTeamData = data;
        }
        if (!isEqualLogo && logo) {
          const { data } = await updateLogoRequest(selectedCompanyId, teamDataId, logo);
          newTeamData = data;
        }

        if (newTeamData) {
          await updateChat({
            companyId: selectedCompanyId,
            entityId: teamDataId,
            name: newTeamData.name,
            description: newTeamData.description,
            avatarUrl: newTeamData.logo?.url,
            userId: user.uuid,
          });
        }

        setTeamData(newTeamData);
        showNotification({ message: 'Team has been updated', options: { variant: 'success' } });
      } catch (e) {
        handleServerError(e);
      }
    },
    [selectedCompanyId, teamDataId, teamData]
  );

  const getTeam = useCallback(async () => {
    const teamUrlItems = getUrlItems(team);
    const teamId = teamUrlItems?.id;
    if (!selectedCompanyId || !teamId) {
      return;
    }
    try {
      const { data: teamData } = await getTeamRequest(selectedCompanyId, teamId);
      // getTeamRequest → TeamRetrieve (no member_count); client Team requires it.
      setTeamData(teamData as unknown as Team);
      if (teamData?.logo) {
        setLogo({
          url: teamData.logo.url,
          file: null,
        });
      }
      methods.setValue('name', teamData.name);
      // logo from gateway is LogoRead { s3_key, url }; form field holds Image { file, url, name? }.
      methods.setValue('logo', teamData.logo as unknown as Image);
      methods.setValue('description', teamData?.description ?? '');
      methods.setValue('location_ids', teamData?.location_ids ?? []);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  }, [selectedCompanyId, team, methods]);

  const deleteTeam = useCallback(async () => {
    if (!selectedCompanyId || !teamDataId) {
      return;
    }
    try {
      setLoading(true);
      await deleteLogoRequest(selectedCompanyId, teamDataId);
      await deleteTeamRequest(selectedCompanyId, teamDataId);
      setLoading(false);
      removeChat({
        companyId: selectedCompanyId,
        entityId: teamDataId,
        userId: user.uuid,
      });
      history.push('/teams/list');
    } catch (e) {
      handleServerError(e);
      setLoading(false);
    }
  }, [history, selectedCompanyId, teamDataId, user.uuid]);

  useEffect(() => {
    getTeam();
  }, []);

  useEffect(() => {
    getTeamMembers();
  }, [getTeamMembers]);

  return {
    open,
    logo,
    total,
    loading,
    sortModel,
    substring,
    methods,
    teamData,
    pathname,
    handleSearchChange,
    openDeleteTeamDialog,
    selectionModel,
    selectedCompanyId,
    teamId: teamDataId,
    setOffset,
    handleSortModelChange,
    handleSubmit,
    handlePageChange,
    setOpen,
    setLogo,
    setOpenDeleteTeamDialog,
    deleteTeam,
    addTeamMembersRequest,
    setSelectionModel,
    getTeamsCandidatesRequest,
    getTeamMembersRequest,
    deleteTeamMembersRequest,
    changeTeamManagersRequest,
    getStaffRequest,
    removeUsersFromChat,
    setTeamData,
  };
};
